import React, { Suspense, useEffect } from "react";

import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useParams, useLocation } from "react-router-dom";

/** Scrolls to top on pathname change, excluding /square and modal sub-routes */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith('/square')) return;
    if (pathname.includes('/post/') || pathname.includes('/photos')) return;
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
import { ThemeProvider } from "next-themes";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useQuery } from '@tanstack/react-query';
import {
  DashboardPageSkeleton,
  ClassroomPageSkeleton,
  ExplorePageSkeleton,
  ProfilePageSkeleton,
  
  NotificationsPageSkeleton,
  SquarePageSkeleton,
  NotebookPageSkeleton,
  NoteEditorPageSkeleton,
  MessagesPageSkeleton,
  SettingsPageSkeleton,
  AdminPageSkeleton,
  CourseAdminPageSkeleton,
  CourseOverviewPageSkeleton,
  LessonViewerPageSkeleton,
} from "@/components/templates/skeletons";
import { EditorPanelSkeleton } from "@/components/molecules/skeletons";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { PersistentLayout } from "@/components/templates/DashboardLayout";
import { AuthProvider } from "@/components/AuthProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PostModalProvider } from "@/contexts/PostModalContext";
import { PhotoModalProvider } from "@/contexts/PhotoModalContext";

const GlobalPostModal = React.lazy(() =>
  import("@/components/GlobalPostModal").then(m => ({ default: m.GlobalPostModal }))
);
const GlobalPhotoModalRouter = React.lazy(() =>
  import("@/components/GlobalPhotoModalRouter").then(m => ({ default: m.GlobalPhotoModalRouter }))
);

import { RealtimeAuthProvider } from '@/contexts/RealtimeAuthContext';

import { CourseLayout } from "@/components/templates/CourseLayout";
import { createLazyRoute } from "@/lib/route-preloader-enhanced";
import { RoleGuard } from "@/components/RoleGuard";
import { AdminEditorMobileGuard } from "@/components/atoms/AdminEditorMobileGuard";
import { RoutePreloader } from "@/components/RoutePreloader";
import { usePlatformConfig } from "@/hooks/usePlatformConfig";
import { fetchMyRoles } from "@/hooks/useUserRole";
import { useAuth } from "@/hooks/useAuth";
import { queryKeys } from "@/utils/queryKeys";
import MaintenancePage from "@/pages/MaintenancePage";

import { cleanupOldDrafts } from "@/utils/draftCleanup";
import { reportLegacyServiceWorkers, reportTombstoneRefusals } from "@/lib/service-workers";

const NotFound = React.lazy(() => import("./pages/NotFound"));

// Optimized lazy loading with intelligent preloading
const Index = createLazyRoute('index');
const Dashboard = createLazyRoute('dashboard');
const MonitoringRequests = React.lazy(() => import('./pages/MonitoringRequests'));
const Classroom = createLazyRoute('classroom');
const Explore = createLazyRoute('explore');

const CourseOverview = createLazyRoute('courseOverview');
const LessonViewer = createLazyRoute('lessonViewer');
const QuizViewerPage = createLazyRoute('quizViewer');
const Notebook = createLazyRoute('notebook');
const NoteEditor = createLazyRoute('noteEditor');
const Square = createLazyRoute('square');


const Notifications = createLazyRoute('notifications');
const Messages = createLazyRoute('messages');
const User = createLazyRoute('user');
const Course = createLazyRoute('course');
const SuperAdmin = createLazyRoute('superAdmin');
const Settings = createLazyRoute('settings');
const Profile = createLazyRoute('profile');

// Admin editor routes (deep-linkable nested admin shells)
const LessonEditorRoute = React.lazy(() => import('./pages/admin/LessonEditorRoute'));
const QuizEditorRoute = React.lazy(() => import('./pages/admin/QuizEditorRoute'));
const OntologyEditorRoute = React.lazy(() => import('./pages/admin/OntologyEditorRoute'));
const StudentPreviewRoute = React.lazy(() => import('./pages/StudentPreviewRoute'));

/** Maintenance gate — blocks all routes for non-super-admins when maintenance_mode is enabled.
 *  Fail open: if config or role fetch errors out, the app works normally.
 *  Fail closed during load: show nothing until we know the config state.
 *
 *  The landing/login page (/) is always allowed through so super admins can
 *  sign in to disable maintenance mode.
 *
 *  Uses a direct role query with throwOnError: false instead of useUserRole()
 *  (which has throwOnError: true). This avoids wrapping children in an ErrorBoundary,
 *  which would map any unrelated app crash to the maintenance page. Same queryKey as
 *  useUserRole, so the cache is shared — no extra network requests. */
function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const { data: maintenanceMode, isLoading: configLoading, isError: configError } = usePlatformConfig('maintenance_mode');
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id;
  const { pathname } = useLocation();

  // Fail-open role query — shares cache with useUserRole() but never throws
  const { data: roles = [], isLoading: roleLoading, isError: roleError } = useQuery({
    queryKey: queryKeys.user.roles(userId ?? ''),
    queryFn: fetchMyRoles,
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    throwOnError: false,
  });

  const isSuperAdmin = roles.includes('super_admin');

  // Still loading config — show nothing to avoid flash
  if (configLoading) return null;

  // Fail open: config error, not in maintenance mode, or role check failed
  if (configError || maintenanceMode !== 'true' || roleError) return <>{children}</>;

  // Always allow the landing/login page so super admins can sign in
  if (pathname === '/') return <>{children}</>;

  // Wait for auth to resolve before determining access for non-login routes
  if (authLoading) return null;

  // Not authenticated on a non-login route — show maintenance page
  if (!userId) return <MaintenancePage />;

  // While loading role, show nothing briefly (avoid flash)
  if (roleLoading) return null;

  // Super admins bypass maintenance
  if (isSuperAdmin) return <>{children}</>;

  return <MaintenancePage />;
}

/** Redirect legacy ?lesson= search params to path-based routes */
function LegacyLessonRedirect() {
  const [searchParams] = useSearchParams();
  const { subjectId } = useParams();
  const lessonId = searchParams.get('lesson');
  if (lessonId && subjectId) {
    return <Navigate to={`/classroom/course/${subjectId}/lesson/${lessonId}`} replace />;
  }
  return (
    <Suspense fallback={<CourseOverviewPageSkeleton />}>
      <CourseOverview />
    </Suspense>
  );
}

const App = () => {
  useEffect(() => {
    cleanupOldDrafts();
    void reportLegacyServiceWorkers();
    return reportTombstoneRefusals();
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider attribute="class" defaultTheme="light">
        <AuthProvider>
          <RealtimeAuthProvider>
          <TooltipProvider>
            <Sonner />
            
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <PostModalProvider>
                <PhotoModalProvider>
                  <Suspense fallback={null}>
                    <ErrorBoundary
                      fallback={null}
                      onError={(error) => {
                        // Round 6 — log on every non-prod TIER (so dev.iskool.ai
                        // surfaces these errors in the console), not Vite build mode.
                        if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
                          console.error('[App] GlobalPostModal error:', error);
                        }
                      }}
                    >
                      <GlobalPostModal />
                    </ErrorBoundary>
                    <ErrorBoundary
                      fallback={null}
                      onError={(error) => {
                        if (import.meta.env.VITE_DEPLOY_ENV !== 'production') {
                          console.error('[App] GlobalPhotoModalRouter error:', error);
                        }
                      }}
                    >
                      <GlobalPhotoModalRouter />
                    </ErrorBoundary>
                  </Suspense>
                  <MaintenanceGate>
                  <ScrollToTop />
                     <RoutePreloader>
                  <Routes>
                 {/* Landing page without layout */}
                 <Route path="/" element={
                    <Suspense fallback={<div className="min-h-screen bg-background" />}>
                      <Index />
                    </Suspense>
                 } />
                 
                 
                 {/* Protected routes with persistent layout */}
                 
                 {/* Protected routes with persistent layout */}
                <Route element={
                  <ProtectedRoute>
                    <SidebarProvider>
                      <PersistentLayout />
                    </SidebarProvider>
                  </ProtectedRoute>
                }>
                  <Route path="/dashboard" element={
                    <Suspense fallback={<DashboardPageSkeleton />}>
                      <Dashboard />
                    </Suspense>
                  } />
                  <Route path="/monitoring-requests" element={
                    <Suspense fallback={<DashboardPageSkeleton />}>
                      <MonitoringRequests />
                    </Suspense>
                  } />

                  <Route path="/classroom" element={
                    <Suspense fallback={<ClassroomPageSkeleton />}>
                      <Classroom />
                    </Suspense>
                  } />
                  
                  <Route path="/explore" element={
                    <Suspense fallback={<ExplorePageSkeleton />}>
                      <Explore />
                    </Suspense>
                  } />
                  
                  <Route path="/profile" element={
                    <Suspense fallback={<ProfilePageSkeleton />}>
                      <Profile />
                    </Suspense>
                  } />
                  
                  <Route path="/profile/:username" element={
                    <Suspense fallback={<ProfilePageSkeleton />}>
                      <Profile />
                    </Suspense>
                  } />
                  
                  <Route path="/profile/:username/photos" element={
                    <Suspense fallback={<ProfilePageSkeleton />}>
                      <Profile />
                    </Suspense>
                  } />
                  
                  <Route path="/profile/:username/post/:postId" element={
                    <Suspense fallback={<ProfilePageSkeleton />}>
                      <Profile />
                    </Suspense>
                  } />
                  
                  <Route path="/profile/:username/post/:postId/photo/:photoId" element={
                    <Suspense fallback={<ProfilePageSkeleton />}>
                      <Profile />
                    </Suspense>
                  } />
                  
                   {/* /circle redirects to profile circle tab */}
                   <Route path="/circle" element={<Navigate to="/profile?tab=circle" replace />} />
                  
                   {/* /connection-requests redirects to profile circle tab */}
                   <Route path="/connection-requests" element={<Navigate to="/profile?tab=circle" replace />} />
                  
                  <Route path="/notifications" element={
                    <Suspense fallback={<NotificationsPageSkeleton />}>
                      <Notifications />
                    </Suspense>
                  } />
                  
                  <Route path="/square" element={
                    <Suspense fallback={<SquarePageSkeleton />}>
                      <Square />
                    </Suspense>
                  } />
                  
                  <Route path="/notebook" element={
                    <Suspense fallback={<NotebookPageSkeleton />}>
                      <Notebook />
                    </Suspense>
                  } />
                  
                  <Route path="/notebook/new" element={
                    <Suspense fallback={<NoteEditorPageSkeleton />}>
                      <NoteEditor />
                    </Suspense>
                  } />
                  
                  <Route path="/notebook/:id" element={
                    <Suspense fallback={<NoteEditorPageSkeleton />}>
                      <NoteEditor />
                    </Suspense>
                  } />
                  
                  <Route path="/messages" element={
                    <Suspense fallback={<MessagesPageSkeleton />}>
                      <Messages />
                    </Suspense>
                  } />
                  
                  <Route path="/settings" element={
                    <Suspense fallback={<SettingsPageSkeleton />}>
                      <Settings />
                    </Suspense>
                  } />
                  
                  <Route path="/user" element={
                    <Suspense fallback={<AdminPageSkeleton />}>
                      <RoleGuard requiredRole="admin">
                        <User />
                      </RoleGuard>
                    </Suspense>
                  } />
                  
                  <Route path="/course" element={
                    <Suspense fallback={<CourseAdminPageSkeleton />}>
                      <RoleGuard requiredRole="courseAdmin">
                        <Course />
                      </RoleGuard>
                    </Suspense>
                  } />
                  <Route path="/course/:subjectId" element={
                    <Suspense fallback={<CourseAdminPageSkeleton />}>
                      <RoleGuard requiredRole="courseAdmin">
                        <Course />
                      </RoleGuard>
                    </Suspense>
                  } />
                  <Route path="/course/:subjectId/lesson/:lessonId" element={
                    <Suspense fallback={<CourseAdminPageSkeleton />}>
                      <RoleGuard requiredRole="courseAdmin">
                        <AdminEditorMobileGuard>
                          <CourseLayout variant="author" />
                        </AdminEditorMobileGuard>
                      </RoleGuard>
                    </Suspense>
                  }>
                    {/* Inner Suspense around the lazy LessonEditorRoute so cold
                        loads swap only the middle-outlet content, not the whole
                        CourseLayout. Without this, the outer Suspense catches
                        the lazy import and unmounts the header + sidebar + tutor
                        pane back to CourseAdminPageSkeleton — jarring on slow
                        networks and first-time nav into the editor. */}
                    <Route index element={
                      <Suspense fallback={<EditorPanelSkeleton />}>
                        <LessonEditorRoute />
                      </Suspense>
                    } />
                  </Route>
                  <Route path="/course/:subjectId/quiz/:quizId" element={
                    <Suspense fallback={<CourseAdminPageSkeleton />}>
                      <RoleGuard requiredRole="courseAdmin">
                        <AdminEditorMobileGuard>
                          <CourseLayout variant="author" />
                        </AdminEditorMobileGuard>
                      </RoleGuard>
                    </Suspense>
                  }>
                    <Route index element={
                      <Suspense fallback={<EditorPanelSkeleton />}>
                        <QuizEditorRoute />
                      </Suspense>
                    } />
                  </Route>
                  <Route path="/course/:subjectId/chapter/:chapterId/ontology" element={
                    <Suspense fallback={<CourseAdminPageSkeleton />}>
                      <RoleGuard requiredRole="courseAdmin">
                        <AdminEditorMobileGuard>
                          <CourseLayout variant="author" />
                        </AdminEditorMobileGuard>
                      </RoleGuard>
                    </Suspense>
                  }>
                    <Route index element={
                      <Suspense fallback={<EditorPanelSkeleton />}>
                        <OntologyEditorRoute />
                      </Suspense>
                    } />
                  </Route>
                  <Route path="/course/:subjectId/preview" element={
                    <Suspense fallback={<CourseAdminPageSkeleton />}>
                      <RoleGuard requiredRole="courseAdmin">
                        <StudentPreviewRoute />
                      </RoleGuard>
                    </Suspense>
                  } />
                  <Route path="/course/:subjectId/preview/:lessonId" element={
                    <Suspense fallback={<CourseAdminPageSkeleton />}>
                      <RoleGuard requiredRole="courseAdmin">
                        <StudentPreviewRoute />
                      </RoleGuard>
                    </Suspense>
                  } />
                  
                  <Route path="/super-admin" element={
                    <Suspense fallback={<AdminPageSkeleton />}>
                      <RoleGuard requiredRole="superAdmin">
                        <SuperAdmin />
                      </RoleGuard>
                    </Suspense>
                  } />

                  {/* FIND-D (2026-04-25): catch-all inside PersistentLayout
                      so a signed-in user who hits an unknown URL still gets
                      the sidebar + header. The outer `path="*"` below is
                      kept for unauthenticated callers (no chrome to render
                      yet) and for routes outside this layout tree. */}
                  <Route path="*" element={<Suspense fallback={null}><NotFound /></Suspense>} />
                </Route>

                {/* Course overview — full-width standalone (not inside CourseLayout) */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/classroom/course/:subjectId" element={<LegacyLessonRedirect />} />
                </Route>

                {/* Lesson/quiz views — three-panel layout via CourseLayout */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/classroom/course/:subjectId" element={<CourseLayout />}>
                    <Route path="lesson/:lessonId" element={
                      <Suspense fallback={<LessonViewerPageSkeleton />}>
                        <LessonViewer />
                      </Suspense>
                    } />
                    <Route path="quiz/:quizId" element={
                      <Suspense fallback={<LessonViewerPageSkeleton />}>
                        <QuizViewerPage />
                      </Suspense>
                    } />
                  </Route>
                </Route>
                
                <Route path="*" element={<Suspense fallback={null}><NotFound /></Suspense>} />
                </Routes>
              </RoutePreloader>
                  </MaintenanceGate>
                </PhotoModalProvider>
              </PostModalProvider>
            </BrowserRouter>
          </TooltipProvider>
          </RealtimeAuthProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
